import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import useMailboxService from 'src/modules/mailer/hooks/useMailboxService';
import useMailerDriver from 'src/modules/mailer/hooks/useMailerDriver';
import {
    MAILBOX_FOLDER_API,
    MAILBOX_PAGE_SIZE,
    normalizeMailboxListResponse,
} from '../../../helpers';

export const MAILBOX_MESSAGES_QUERY_KEY = 'mailbox-fetch-messages';

const useMailboxMessages = ({
    t,
    folder,
    enableFlags = false,
}) => {
    const service = useMailboxService();
    const { active } = useMailerDriver();
    const [searchInput, setSearchInput] = useState('');
    const [q, setQ] = useState('');
    const [flag, setFlag] = useState('');
    const [page, setPage] = useState(1);
    const apiFolder = MAILBOX_FOLDER_API[folder] || MAILBOX_FOLDER_API.inbox;

    useEffect(() => {
        setSearchInput('');
        setQ('');
        setFlag('');
        setPage(1);
    }, [folder]);

    useEffect(() => {
        const nextQuery = searchInput.trim();
        const timer = window.setTimeout(() => {
            setQ((current) => {
                if (current !== nextQuery) {
                    setPage(1);
                }

                return nextQuery;
            });
        }, 300);

        return () => window.clearTimeout(timer);
    }, [searchInput]);

    const query = useQuery({
        queryKey: [MAILBOX_MESSAGES_QUERY_KEY, active, apiFolder, page, q, flag],
        queryFn: async () => {
            const params = {
                folder: apiFolder,
                page,
                limit: MAILBOX_PAGE_SIZE,
            };

            if (q) {
                params.q = q;
            }

            if (enableFlags && flag) {
                params.flag = flag;
            }

            const payload = await service.fetchMessages(params);

            return {
                ...normalizeMailboxListResponse(payload),
                folder: apiFolder,
            };
        },
        cacheTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('An error occurred while fetching mailbox messages.'), {
                type: 'error',
            });
        },
    });

    const handleFlagChange = (nextFlag) => {
        if (!enableFlags) {
            return;
        }

        setFlag((current) => (current === nextFlag ? '' : nextFlag));
        setPage(1);
    };

    const isPreviousFolder = Boolean(query.data?.folder && query.data.folder !== apiFolder);
    const data = isPreviousFolder ? undefined : query.data;
    const messages = data?.messages || [];
    const total = data?.total || 0;
    const limit = data?.limit || MAILBOX_PAGE_SIZE;
    let totalPages = total ? Math.ceil(total / limit) : page;

    if (messages.length === limit && page >= totalPages) {
        totalPages = page + 1;
    }

    return {
        searchInput,
        setSearchInput,
        flag,
        handleFlagChange,
        page,
        setPage,
        messages,
        total,
        totalPages,
        limit,
        isLoading: (query.isLoading || query.isFetching) && !messages.length,
        isFetching: query.isFetching,
        isError: query.isError,
        refetch: query.refetch,
    };
};

export default useMailboxMessages;
